import { UserProfile } from 'fitbeat.gym-core';
import { TimeSpan } from 'fitbeat.models';
import moment from 'moment-timezone';
import React, { FunctionComponent, ReactElement } from 'react';
import { StyleSheet, Text, TouchableHighlight, View } from 'react-native';
import Theme from '../../theme';
import { getTimezoneFromGymName } from '../../utils/gyms';
import { defaultScale } from '../../utils/scaling';
import { TileType, WorkoutSectionTile } from '../plannerSelectors';
import PlannerInfluencerBooking from './PlannerInfluencerBooking';
import { WorkoutBooking } from './WorkoutBooking';

interface IProps {
  day: moment.Moment;
  onPress: (booking) => void;
  entries: WorkoutSectionTile[];
  userProfile: UserProfile;
  t: (key: string) => string;
}

export const PlannerDay: FunctionComponent<IProps> = (
  {
    day, // Device start of day utc
    onPress,
    entries,
    userProfile,
    t,
  },
) => {

  const isSameDay = (entryDate: Date): boolean => {
    return moment(entryDate).isSame(day, 'day');
  };

  const entryComponents: ReactElement[] = [];
  entries.forEach((entry, index) => {
      if (entry.type === TileType.workoutSession && isSameDay(entry.data.startTimeOfFirstExercise)) {
        entryComponents.push(
          <WorkoutBooking
            booking={entry.data}
            t={t}
            onPress={onPress}
            key={entry.data.uniqueReference}
            itemIndex={index}
          />,
        );
      } else if (
        entry.type === TileType.influencerInviteTile &&
        isSameDay(entry.data.workoutSession.startTimeOfFirstExercise)
      ) {
        entryComponents.push(
          <PlannerInfluencerBooking
            userProfile={userProfile}
            workoutSession={entry.data.workoutSession}
            t={t}
            key={entry.data.workoutSession.uniqueReference}
            itemIndex={index}
            influencerName={entry.data.influencerDetails.fullname}
            livestreamOptions={entry.data.workoutSession.livestreamOptions}
          />,
        );
      } else if (
        entry.type === TileType.bookedInfluencerInviteTile &&
        isSameDay(entry.data.workoutSession.startTimeOfFirstExercise)
      ) {
        entryComponents.push(
          <PlannerInfluencerBooking
            userProfile={userProfile}
            workoutSession={entry.data.workoutSession}
            t={t}
            key={entry.data.workoutSession.uniqueReference}
            itemIndex={index}
            influencerName={entry.data.influencerData.name}
            livestreamOptions={entry.data.workoutSession.livestreamOptions}
            booked
          />,
        );
      } else if (entry.type === TileType.inviteTile && isSameDay(entry.data.startTimeOfFirstExercise)) {
        entryComponents.push(
          <WorkoutBooking
            booking={entry.data}
            t={t}
            onPress={onPress}
            key={entry.data.uniqueReference}
            itemIndex={index}
            isInvite
          />,
        );
      }
    },
  );

  return (
    <>
      <TouchableHighlight onPress={() => onPress(null)}
                          activeOpacity={1}
                          underlayColor={Theme.GREY_LIGHT}>
        <View style={styles.day}>
          <View style={styles.dateContainer}>
            <Text style={styles.dayName}>{day.format('ddd')}</Text>
            <Text style={styles.date}>{`${day.date()} ${day.format('MMM')}`}</Text>
          </View>
          <View style={styles.dayContentContainer}>
            {!entryComponents.length &&
              <Text style={styles.emptyStateText}>{t('noWorkoutScheduled')}</Text>
            }
            {entryComponents}
          </View>
        </View>
      </TouchableHighlight>
      <View style={styles.divider} />
    </>
  );
};

const styles = StyleSheet.create({
  day: {
    minHeight: defaultScale(50),
    overflow: 'hidden',
    flexDirection: 'row',
    paddingHorizontal: defaultScale(15),
    paddingVertical: defaultScale(10),
  },
  divider: {
    backgroundColor: Theme.GREY_LIGHT,
    height: defaultScale(5),
  },
  dateContainer: {
    width: defaultScale(50),
    paddingVertical: defaultScale(10),
  },
  dayName: {
    ...Theme.textStyle.bodyCopyBold,
  },
  date: {
    ...Theme.textStyle.small,
  },
  emptyStateText: {
    ...Theme.textStyle.bodyCopyReg,
    textAlign: 'center',
  },
  dayContentContainer: {
    flex: 1,
    justifyContent: 'center',
  },
});
